import { useSuspenseQuery } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';
import { createPalette, type PaletteCommandDef, usePaletteTrigger } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { BreadcrumbButton, BreadcrumbItem } from '@gitbook/spine-react';

import {
    type GetSitePaletteFactoryProps,
    getSitePaletteFactory,
    SiteContentIcon,
    useSpaceSites,
} from '../../../Components';
import { useNavigation } from '../../../navigation';
import { apiQuery } from '../../../state';

/**
 * Breadcrumb item to navigate to the site(s) containing the space.
 */
export function SpaceSiteBreadcrumbItem(props: { space: api.Space }) {
    const { space } = props;
    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId: space.organization,
        })
    );
    const sites = useSpaceSites(organizationContent, space.id);

    if (sites.length === 0) {
        return null;
    }

    const singleSite = sites.length === 1 ? sites[0] : null;

    if (singleSite) {
        return (
            <BreadcrumbItem>
                <SingleSiteButton organizationId={space.organization} site={singleSite.site} />
            </BreadcrumbItem>
        );
    }

    return (
        <BreadcrumbItem>
            <MultiSitesButton organizationId={space.organization} sites={sites} />
        </BreadcrumbItem>
    );
}

function SingleSiteButton(props: { site: api.Site; organizationId: string }) {
    const { site, organizationId } = props;
    const { navigate } = useNavigation();
    const paletteTrigger = usePaletteTrigger({
        palette: getSitePaletteFactory({
            site: site,
            organizationId,
            navigate,
        }),
    });

    return (
        <BreadcrumbButton
            {...paletteTrigger.triggerProps}
            icon={<SiteContentIcon site={site} />}
            hoverIcon={Icon.ChevronDown}
            isActive={false}
        />
    );
}

function MultiSitesButton(props: {
    organizationId: string;
    sites: Array<{
        site: api.Site;
        siteSpace: api.SiteSpace;
    }>;
}) {
    const { sites, organizationId } = props;
    const { navigate } = useNavigation();
    const paletteTrigger = usePaletteTrigger({
        palette: () =>
            createPalette({
                groups: [
                    {
                        key: 'sites',
                        commands: sites.map((site) =>
                            getSiteCommand({ site: site.site, navigate, organizationId })
                        ),
                    },
                ],
            }),
    });

    return (
        <BreadcrumbButton
            {...paletteTrigger.triggerProps}
            icon={Icon.DocsSite}
            hoverIcon={Icon.ChevronDown}
            isActive={false}
        />
    );
}

function getSiteCommand(props: GetSitePaletteFactoryProps): PaletteCommandDef {
    const { site, organizationId, navigate } = props;
    return {
        key: site.id,
        title: site.title,
        icon: <SiteContentIcon site={site} />,
        descendant: getSitePaletteFactory({
            site: site,
            organizationId,
            navigate,
        }),
    };
}
